<template>
  <v-container fluid class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h2 class="d-flex align-center">Catalogo de productos <HelpHint text="ABM de artículos con datos comerciales y logísticos. Estos datos impactan ventas, stock y precios." /></h2>
        <p>Administra productos, observaciones e imagenes.</p>
      </div>
      <v-btn color="primary" class="hero-btn" @click="openCreate">Nuevo articulo</v-btn>
    </div>

    <v-card outlined class="page-card">
      <div class="table-header">
        <div>
          <h3>Listado</h3>
          <p>{{ products.length }} productos</p>
        </div>
        <div class="table-actions">
          <v-text-field
            v-model="query"
            dense
            outlined
            hide-details
            placeholder="Buscar producto"
            class="table-search"
          />
          <v-select
            v-model="statusFilter"
            :items="statusFilterOptions"
            item-text="text"
            item-value="value"
            dense
            outlined
            hide-details
            label="Estado"
            class="status-filter"
          />
          <v-btn color="primary" :loading="loading" @click="fetchProducts">Buscar</v-btn>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="products"
        :loading="loading"
        class="elevation-0"
        item-key="id"
        dense
        :footer-props="{
          'items-per-page-options': [20, 50, 100, -1],
          'items-per-page-text': 'Filas por página:'
        }"
      >
        <!-- Custom Column Headers with Explanatory Tooltips -->
        <template v-slot:header.standardCost="{ header }">
          <span class="font-weight-bold text-uppercase" style="font-size: 11px; letter-spacing: 0.05em;">{{ header.text }}</span>
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on" style="font-size: 14px;">mdi-help-circle-outline</v-icon>
            </template>
            <span>Costo base del producto en pesos (ARS).</span>
          </v-tooltip>
        </template>

        <template v-slot:header.utilityCoefficient="{ header }">
          <span class="font-weight-bold text-uppercase" style="font-size: 11px; letter-spacing: 0.05em;">{{ header.text }}</span>
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on" style="font-size: 14px;">mdi-help-circle-outline</v-icon>
            </template>
            <span>Coeficiente multiplicador de ganancia (markup) sobre el costo.</span>
          </v-tooltip>
        </template>

        <template v-slot:header.brand="{ header }">
          <span class="font-weight-bold text-uppercase" style="font-size: 11px; letter-spacing: 0.05em;">{{ header.text }}</span>
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on" style="font-size: 14px;">mdi-help-circle-outline</v-icon>
            </template>
            <span>Marca o fabricante comercial del artículo.</span>
          </v-tooltip>
        </template>

        <template v-slot:header.canBeSold="{ header }">
          <span class="font-weight-bold text-uppercase" style="font-size: 11px; letter-spacing: 0.05em;">{{ header.text }}</span>
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on" style="font-size: 14px;">mdi-help-circle-outline</v-icon>
            </template>
            <span>Determina si está autorizado para la venta comercial directa al público.</span>
          </v-tooltip>
        </template>

        <template v-slot:header.isActive="{ header }">
          <span class="font-weight-bold text-uppercase" style="font-size: 11px; letter-spacing: 0.05em;">{{ header.text }}</span>
          <v-tooltip bottom max-width="260">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on" style="font-size: 14px;">mdi-help-circle-outline</v-icon>
            </template>
            <span>Estado del producto en el inventario.</span>
          </v-tooltip>
        </template>

        <template v-slot:item.utilityCoefficient="{ item }">
          {{ formatUtilityCoefficient(item.utilityCoefficient) }}
        </template>
        <template v-slot:item.isActive="{ item }">
          <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']" style="font-size: 11px; padding: 2px 8px; font-weight: 700;">
            {{ item.isActive ? 'Activo' : 'Inactivo' }}
          </span>
        </template>
        <template v-slot:item.status="{ item }">
          <span :class="['pill', statusMeta(item.status).pill]">{{ statusMeta(item.status).text }}</span>
        </template>
        <template v-slot:item.canSell="{ item }">{{ item.canSell ? "Sí" : "No" }}</template>
        <template v-slot:item.standardCost="{ item }">
          $ {{ formatNumber(item.standardCost) }}
        </template>
        <template v-slot:item.canBeSold="{ item }">
          <span :class="['pill', item.canBeSold ? 'pill--success' : 'pill--danger']" style="font-size: 11px; padding: 2px 8px; font-weight: 700;">
            {{ item.canBeSold ? 'Sí' : 'No' }}
          </span>
        </template>
        <template v-slot:item.origin="{ item }">
          <v-chip x-small :color="item.origin === 'IMPORTADO' ? 'orange' : 'grey lighten-1'" dark label>
            {{ item.origin === 'IMPORTADO' ? 'IMP' : 'NAC' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click="openEdit(item)">
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
          <v-btn icon small color="secondary" @click="openImage(item)">
            <v-icon small>mdi-image-outline</v-icon>
          </v-btn>
          <v-btn icon small color="teal" @click="showPurchaseOrders(item)">
            <v-icon small>mdi-file-document-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="640">
      <v-card>
        <v-card-title>{{ isEditing ? 'Editar producto' : 'Crear producto' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.code" label="Codigo" outlined dense :rules="requiredRules" />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.unitCode" label="Unidad" outlined dense />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.standardCost"
                  type="number"
                  label="Costo estándar (ARS)"
                  outlined
                  dense
                  prepend-inner-icon="mdi-currency-usd"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.salesTaxCode"
                  :items="salesTaxOptions"
                  item-text="text"
                  item-value="value"
                  label="IVA de venta"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.utilityCoefficient"
                  type="number"
                  min="0"
                  step="0.0001"
                  label="Coeficiente de utilidad"
                  outlined
                  dense
                  hint="Ejemplo: 1.2500 = 25% sobre costo"
                  persistent-hint
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.barcode" label="Código de Barras (EAN)" outlined dense prepend-inner-icon="mdi-barcode-scan" />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.productType"
                  :items="productTypeOptions"
                  label="Tipo de producto"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="form.weight" type="number" label="Peso (kg)" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="form.volume" type="number" label="Volumen (m3)" outlined dense />
              </v-col>
            </v-row>
             <v-row dense class="mb-2">
               <v-col cols="12" md="6">
                 <v-checkbox v-model="form.canBeSold" label="Habilitado para la venta (Vendible)" dense hide-details />
               </v-col>
               <v-col cols="12" md="6">
                 <v-checkbox v-model="form.canBePurchased" label="Habilitado para compra a proveedores" dense hide-details />
               </v-col>
             </v-row>
            <v-text-field v-model="form.name" label="Nombre" outlined dense :rules="requiredRules" />
            <v-text-field
              v-model="form.shortDescription"
              label="Descripcion corta"
              outlined
              dense
            />
            <v-textarea v-model="form.description" label="Observaciones" outlined dense rows="2" />
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.brand" label="Marca" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.location" label="Ubicacion" outlined dense />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-combobox
                  v-model="selectedRubro"
                  :items="rubroOptions"
                  :loading="rubrosLoading"
                  item-text="label"
                  item-value="value"
                  return-object
                  clearable
                  label="Rubro"
                  outlined
                  dense
                  @update:search-input="onRubroSearch"
                  @change="onRubroChanged"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-combobox
                  v-model="selectedSubrubro"
                  :items="subrubroOptions"
                  :loading="subrubrosLoading"
                  item-text="label"
                  item-value="value"
                  return-object
                  clearable
                  label="Subrubro"
                  outlined
                  dense
                  @update:search-input="onSubrubroSearch"
                  @change="onSubrubroChanged"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  item-text="text"
                  item-value="value"
                  label="Estado comercial"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.origin"
                  :items="[{text:'Nacional',value:'NACIONAL'},{text:'Importado',value:'IMPORTADO'}]"
                  item-text="text"
                  item-value="value"
                  label="Origen"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.lineCode" label="Linea" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.lineComCode" label="Linea comercial" outlined dense />
              </v-col>
            </v-row>
            <v-switch v-model="form.isActive" label="Producto activo" inset />
          </v-form>
          <div v-if="error" class="error-text">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveProduct">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="imageDialog" max-width="520">
      <v-card>
        <v-card-title>Imagen del producto</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="imageFile"
            label="Seleccionar imagen"
            outlined
            dense
            accept="image/*"
          />
          <div class="image-actions">
            <v-btn color="primary" :disabled="!imageFile" :loading="imageLoading" @click="uploadImage">
              Subir imagen
            </v-btn>
            <v-btn text color="primary" :disabled="!selectedProduct" @click="viewImage">
              Ver imagen
            </v-btn>
            <v-btn text color="error" :disabled="!selectedProduct" @click="deleteImage">
              Eliminar imagen
            </v-btn>
          </div>
          <v-img v-if="imageUrl" :src="imageUrl" max-height="240" contain class="mt-4" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeImageDialog">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="poDialog" max-width="700">
      <v-card>
        <v-card-title class="subtitle-1">Órdenes de Compra - {{ selectedProductName }}</v-card-title>
        <v-card-text>
          <v-data-table :headers="poHeaders" :items="poRows" :loading="poLoading" dense>
            <template v-slot:item.orderDate="{ item }">{{ item.orderDate }}</template>
            <template v-slot:item.quantity="{ item }">{{ item.quantity }}</template>
            <template v-slot:item.receivedQuantity="{ item }">{{ item.receivedQuantity }}</template>
            <template v-slot:item.unitPrice="{ item }">$ {{ formatNumber(item.unitPrice) }}</template>
            <template v-slot:item.status="{ item }">
              <v-chip x-small :color="item.status === 'completed' ? 'success' : item.status === 'cancelled' ? 'error' : 'warning'" dark label>{{ item.status }}</v-chip>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="poDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { productsService } from "../services/products";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "StockCatalog",
  components: { HelpHint },
  data() {
    return {
      products: [],
      loading: false,
      dialog: false,
      imageDialog: false,
      isEditing: false,
      valid: false,
      saving: false,
      error: "",
      query: "",
      statusFilter: "all",
      imageFile: null,
      imageUrl: "",
      imageLoading: false,
      poDialog: false,
      poLoading: false,
      poRows: [],
      selectedProductName: "",
      selectedProduct: null,
      selectedRubro: null,
      selectedSubrubro: null,
      rubroOptions: [],
      rubrosLoading: false,
      subrubroOptions: [],
      subrubrosLoading: false,
      headers: [
        { text: "Código", value: "code" },
        { text: "Nombre", value: "name" },
        { text: "Origen", value: "origin", align: "center" },
        { text: "Costo", value: "standardCost" },
        { text: "Coef.", value: "utilityCoefficient" },
        { text: "Marca", value: "brand" },
        { text: "Vendible", value: "canBeSold", align: "center" },
        { text: "Estado", value: "isActive" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      poHeaders: [
        { text: "OC #", value: "docNumber" },
        { text: "Fecha", value: "orderDate" },
        { text: "Cantidad", value: "quantity" },
        { text: "Recibido", value: "receivedQuantity" },
        { text: "P.Unit", value: "unitPrice" },
        { text: "Estado", value: "status" },
      ],
      productTypeOptions: [
        { text: "Almacenable", value: "product" },
        { text: "Consumible", value: "consu" },
        { text: "Servicio", value: "service" },
      ],
      statusFilterOptions: [
        { text: "Todos", value: "all" },
        { text: "0 - Baja (solo venta)", value: 0 },
        { text: "1 - Activo", value: 1 },
        { text: "2 - Sin reposición", value: 2 },
        { text: "3 - Baja (no vender)", value: 3 },
      ],
      statusOptions: [
        { text: "0 - Baja (solo venta)", value: 0 },
        { text: "1 - Activo", value: 1 },
        { text: "2 - Sin reposición", value: 2 },
        { text: "3 - Baja (no vender)", value: 3 },
      ],
      form: {
        id: "",
        code: "",
        name: "",
        shortDescription: "",
        description: "",
        unitCode: "",
        utilityCoefficient: "",
        brand: "",
        rubro: "",
        subrubro: "",
        lineCode: "",
        lineComCode: "",
        location: "",
        status: 1,
        origin: "NACIONAL",
        isActive: true,
        standardCost: 0,
        salesTaxCode: "IVA_21",
        barcode: "",
        productType: "product",
        weight: 0,
        volume: 0,
        canBeSold: true,
        canBePurchased: true,
      },
      requiredRules: [(v) => Boolean(v) || "Campo requerido"],
      salesTaxOptions: [
        { text: "IVA 0%", value: "IVA_0" },
        { text: "IVA 2,5%", value: "IVA_2_5" },
        { text: "IVA 5%", value: "IVA_5" },
        { text: "IVA 10,5%", value: "IVA_10_5" },
        { text: "IVA 21%", value: "IVA_21" },
        { text: "IVA 27%", value: "IVA_27" }
      ],
    };
  },
  created() {
    Promise.all([this.fetchProducts(), this.fetchRubroOptions()]);
  },
  methods: {
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : [];
    },
    normalizeCatalogValue(value) {
      if (value === null || value === undefined) return "";
      if (typeof value === "string") return value.trim();
      if (typeof value === "object") {
        if (value.value !== undefined && value.value !== null) return String(value.value).trim();
        if (value.code !== undefined && value.code !== null) return String(value.code).trim();
        if (value.label !== undefined && value.label !== null) {
          return String(value.label).split(" - ")[0].trim();
        }
      }
      return String(value).trim();
    },
    toRubroOption(item) {
      const code = String(item.code || "").trim();
      const name = String(item.name || "").trim();
      return { value: code, label: name ? `${code} - ${name}` : code };
    },
    toSubrubroOption(item) {
      const code = String(item.code || "").trim();
      const name = String(item.name || "").trim();
      return { value: code, label: name ? `${code} - ${name}` : code };
    },
    syncSelectedRubro() {
      const code = this.normalizeCatalogValue(this.form.rubro);
      if (!code) {
        this.selectedRubro = null;
        return;
      }
      const found = this.rubroOptions.find((x) => String(x.value).trim() === code);
      this.selectedRubro = found || { value: code, label: code };
    },
    syncSelectedSubrubro() {
      const code = this.normalizeCatalogValue(this.form.subrubro);
      if (!code) {
        this.selectedSubrubro = null;
        return;
      }
      const found = this.subrubroOptions.find((x) => String(x.value).trim() === code);
      this.selectedSubrubro = found || { value: code, label: code };
    },
    async fetchRubroOptions(q = "") {
      this.rubrosLoading = true;
      try {
        const params = { limit: 400 };
        if (q) params.q = q;
        const response = await productsService.getRubros(params);
        const rows = this.unwrap(response) || [];
        this.rubroOptions = rows.map(this.toRubroOption);
        this.syncSelectedRubro();
      } catch (_error) {
        this.rubroOptions = [];
      } finally {
        this.rubrosLoading = false;
      }
    },
    async fetchSubrubroOptions(rubroCode = "", q = "") {
      this.subrubrosLoading = true;
      try {
        const params = { limit: 700 };
        const normalizedRubro = this.normalizeCatalogValue(rubroCode || this.form.rubro);
        if (normalizedRubro) params.rubro = normalizedRubro;
        if (q) params.q = q;
        const response = await productsService.getSubrubros(params);
        const rows = this.unwrap(response) || [];
        this.subrubroOptions = rows.map(this.toSubrubroOption);
        this.syncSelectedSubrubro();
      } catch (_error) {
        this.subrubroOptions = [];
      } finally {
        this.subrubrosLoading = false;
      }
    },
    onRubroSearch(search) {
      this.fetchRubroOptions(search || "");
    },
    onSubrubroSearch(search) {
      this.fetchSubrubroOptions(this.form.rubro, search || "");
    },
    onRubroChanged(value) {
      const rubroCode = this.normalizeCatalogValue(value);
      this.form.rubro = rubroCode;
      this.form.subrubro = "";
      this.selectedSubrubro = null;
      this.syncSelectedRubro();
      this.fetchSubrubroOptions(rubroCode);
    },
    onSubrubroChanged(value) {
      this.form.subrubro = this.normalizeCatalogValue(value);
      this.syncSelectedSubrubro();
    },
    formatUtilityCoefficient(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      return n.toFixed(4);
    },
    async fetchProducts() {
      this.loading = true;
      this.error = "";
      try {
        const params = {};
        if (this.statusFilter !== "all") params.status = this.statusFilter;
        const response = await productsService.list(this.query, params);
        this.products = response.data.data || [];
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar los productos.");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.isEditing = false;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: "",
        code: "",
        name: "",
        shortDescription: "",
        description: "",
        unitCode: "",
        utilityCoefficient: "",
        brand: "",
        rubro: "",
        subrubro: "",
        lineCode: "",
        lineComCode: "",
        location: "",
        status: 1,
        isActive: true,
      };
      this.selectedRubro = null;
      this.selectedSubrubro = null;
      this.subrubroOptions = [];
    },
    openEdit(item) {
      this.isEditing = true;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: item.id,
        code: item.code,
        name: item.name,
        shortDescription: item.shortDescription || "",
        description: item.description || "",
        unitCode: item.unitCode || "",
        utilityCoefficient: item.utilityCoefficient === null || item.utilityCoefficient === undefined ? "" : Number(item.utilityCoefficient),
        brand: item.brand || "",
        rubro: item.rubro || "",
        subrubro: item.subrubro || "",
        lineCode: item.lineCode || "",
        lineComCode: item.lineComCode || "",
        location: item.location || "",
        status: item.status === undefined || item.status === null ? 1 : Number(item.status),
        origin: item.origin || "NACIONAL",
        isActive: Boolean(item.isActive),
        standardCost: item.standardCost || 0,
        salesTaxCode: item.salesTaxCode || "IVA_21",
        barcode: item.barcode || "",
        productType: item.productType || "product",
        weight: item.weight || 0,
        volume: item.volume || 0,
        canBeSold: item.canBeSold !== undefined ? item.canBeSold : true,
        canBePurchased: item.canBePurchased !== undefined ? item.canBePurchased : true,
      };
      this.syncSelectedRubro();
      this.fetchSubrubroOptions(this.form.rubro);
    },
    closeDialog() {
      this.dialog = false;
    },
    async saveProduct() {
      const isValid = this.$refs.form.validate();
      if (!isValid) return;

      this.saving = true;
      this.error = "";

      try {
        if (this.isEditing) {
          const rubro = this.normalizeCatalogValue(this.selectedRubro || this.form.rubro);
          const subrubro = this.normalizeCatalogValue(this.selectedSubrubro || this.form.subrubro);
          await productsService.update(this.form.id, {
            name: this.form.name,
            shortDescription: this.form.shortDescription,
            description: this.form.description,
            observations: this.form.description,
            unitCode: this.form.unitCode,
            utilityCoefficient: this.form.utilityCoefficient === "" || this.form.utilityCoefficient === null
              ? null
              : Number(this.form.utilityCoefficient),
            brand: this.form.brand,
            rubro,
            subrubro,
            lineCode: this.form.lineCode,
            lineComCode: this.form.lineComCode,
            location: this.form.location,
            status: Number(this.form.status),
            origin: this.form.origin,
            isActive: this.form.isActive,
            standardCost: Number(this.form.standardCost),
            salesTaxCode: this.form.salesTaxCode,
            barcode: this.form.barcode,
            productType: this.form.productType,
            weight: Number(this.form.weight),
            volume: Number(this.form.volume),
            canBeSold: this.form.canBeSold,
            canBePurchased: this.form.canBePurchased,
          });
        } else {
          const rubro = this.normalizeCatalogValue(this.selectedRubro || this.form.rubro);
          const subrubro = this.normalizeCatalogValue(this.selectedSubrubro || this.form.subrubro);
          await productsService.create({
            code: this.form.code,
            name: this.form.name,
            shortDescription: this.form.shortDescription,
            description: this.form.description,
            observations: this.form.description,
            unitCode: this.form.unitCode,
            utilityCoefficient: this.form.utilityCoefficient === "" || this.form.utilityCoefficient === null
              ? null
              : Number(this.form.utilityCoefficient),
            brand: this.form.brand,
            rubro,
            subrubro,
            lineCode: this.form.lineCode,
            lineComCode: this.form.lineComCode,
            location: this.form.location,
            status: Number(this.form.status),
            origin: this.form.origin,
            isActive: this.form.isActive,
            standardCost: Number(this.form.standardCost),
            salesTaxCode: this.form.salesTaxCode,
            barcode: this.form.barcode,
            productType: this.form.productType,
            weight: Number(this.form.weight),
            volume: Number(this.form.volume),
            canBeSold: this.form.canBeSold,
            canBePurchased: this.form.canBePurchased,
          });
        }
        this.dialog = false;
        await this.fetchProducts();
      } catch (err) {
        this.error = parseApiError(err, "No pudimos guardar el producto.");
      } finally {
        this.saving = false;
      }
    },
    openImage(item) {
      this.selectedProduct = item;
      this.imageDialog = true;
      this.imageFile = null;
      this.imageUrl = "";
    },
    closeImageDialog() {
      this.imageDialog = false;
      if (this.imageUrl) {
        URL.revokeObjectURL(this.imageUrl);
      }
      this.imageUrl = "";
      this.selectedProduct = null;
    },
    async showPurchaseOrders(item) {
      this.selectedProductName = item.name;
      this.poDialog = true;
      this.poLoading = true;
      try {
        const res = await productsService.getPurchaseOrders(item.id);
        this.poRows = (res.data?.data || res.data || []);
      } catch (err) {
        this.error = parseApiError(err, "No se pudieron cargar las órdenes de compra.");
      } finally {
        this.poLoading = false;
      }
    },
    async uploadImage() {
      if (!this.selectedProduct || !this.imageFile) return;
      this.imageLoading = true;
      try {
        await productsService.uploadImage(this.selectedProduct.id, this.imageFile);
        await this.viewImage();
      } catch (err) {
        this.error = parseApiError(err, "No pudimos subir la imagen.");
      } finally {
        this.imageLoading = false;
      }
    },
    async viewImage() {
      if (!this.selectedProduct) return;
      this.imageLoading = true;
      try {
        const response = await productsService.getImage(this.selectedProduct.id);
        if (this.imageUrl) {
          URL.revokeObjectURL(this.imageUrl);
        }
        this.imageUrl = URL.createObjectURL(response.data);
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar la imagen.");
      } finally {
        this.imageLoading = false;
      }
    },
    async deleteImage() {
      if (!this.selectedProduct) return;
      this.imageLoading = true;
      try {
        await productsService.deleteImage(this.selectedProduct.id);
        if (this.imageUrl) {
          URL.revokeObjectURL(this.imageUrl);
          this.imageUrl = "";
        }
      } catch (err) {
        this.error = parseApiError(err, "No pudimos eliminar la imagen.");
      } finally {
        this.imageLoading = false;
      }
    },
    formatNumber(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "0.00";
      return n.toLocaleString("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    statusMeta(status) {
      const n = Number(status);
      if (n === 0) return { text: "0 - Baja venta", pill: "pill--warn" };
      if (n === 1) return { text: "1 - Activo", pill: "pill--success" };
      if (n === 2) return { text: "2 - Prebaja", pill: "pill--info" };
      return { text: "3 - Baja", pill: "pill--danger" };
    },
  },
};
</script>

<style scoped>
.page {
  padding: 0;
}
.page :deep(tbody td),
.page :deep(thead th) {
  height: 32px !important;
  padding: 2px 8px !important;
  font-weight: 500;
}
.v-data-table :deep(tbody td),
.v-data-table :deep(thead th) {
  height: 32px !important;
  padding: 2px 8px !important;
  font-weight: 500;
}

.page-hero {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.page-hero h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.page-hero p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.hero-btn {
  text-transform: none;
  font-weight: 700;
}

.page-card {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 20px 24px 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.table-header h3 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.table-header p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-search {
  max-width: 240px;
}

.status-filter {
  min-width: 220px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.pill--success {
  background: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.pill--warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.pill--info {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.pill--danger {
  background: rgba(220, 38, 38, 0.12);
  color: #b91c1c;
}

.error-text {
  margin-top: 12px;
  color: #dc2626;
}

.image-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 960px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .table-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
  .table-search {
    max-width: 100%;
  }
  .status-filter {
    min-width: 100%;
  }
}
</style>
